/**
 * @file usePointApi.js
 * @description API composable for point CRUD operations
 * Handles all HTTP requests related to point entities
 */

import { ref, toValue } from 'vue';
import { useQuasar } from 'quasar';
import brickSchemaService from '@/services/brickSchemaService';

/**
 * Composable for point API operations
 * @param {number|Ref<number>} siteId (can be a ref or computed) - The site ID
 * @returns {Object} Point API methods and state
 */
export function usePointApi(siteId) {
  const $q = useQuasar();

  // State
  const loading = ref(false);
  const saving = ref(false);

  /**
   * Load points for equipment or device
   * @param {Object} params - Query parameters
   * @param {number} params.equipment_id - Equipment ID (optional)
   * @param {number} params.device_id - Device ID (optional)
   * @returns {Promise<Array>} Points array
   */
  async function loadPoints(params = {}) {
    try {
      loading.value = true;

      const result = await brickSchemaService.points.getAll(toValue(siteId), params);

      if (!result.success) {
        throw new Error(result.error);
      }

      return result.data.data || result.data || [];
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to load points: ${error.message}`,
      });
      return [];
    } finally {
      loading.value = false;
    }
  }

  /**
   * Create new point
   * @param {Object} pointData - Point data to create
   * @returns {Promise<Object>} Created point
   */
  async function createPoint(pointData) {
    try {
      saving.value = true;

      const result = await brickSchemaService.points.create(toValue(siteId), pointData);

      if (!result.success) {
        throw new Error(result.error);
      }

      const newPoint = result.data.data || result.data;

      $q.notify({
        type: 'positive',
        message: `Point "${pointData.name}" created successfully`,
      });

      return newPoint;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to create point: ${error.message}`,
      });
      throw error;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Update existing point
   * @param {number} pointId - Point ID
   * @param {Object} pointData - Updated point data
   * @returns {Promise<Object>} Updated point
   */
  async function updatePoint(pointId, pointData) {
    try {
      saving.value = true;

      const result = await brickSchemaService.points.update(toValue(siteId), pointId, pointData);

      if (!result.success) {
        throw new Error(result.error);
      }

      const updatedPoint = result.data.data || result.data;

      $q.notify({
        type: 'positive',
        message: 'Point updated successfully',
      });

      return updatedPoint;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to update point: ${error.message}`,
      });
      throw error;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Delete point
   * @param {number} pointId - Point ID to delete
   * @param {boolean} cascade - Delete children recursively
   * @returns {Promise<void>}
   */
  async function deletePoint(pointId, cascade = false) {
    try {
      const result = await brickSchemaService.points.delete(toValue(siteId), pointId, cascade);

      if (!result.success) {
        const error = new Error(result.error);
        // Preserve full response data for cascade dialog detection
        if (result.response) {
          error.response = result.response;
        }
        throw error;
      }
    } catch (error) {
      // Re-throw with response data intact - don't show notification here
      throw error;
    }
  }

  /**
   * Duplicate point
   * @param {number} pointId - Point ID to duplicate
   * @param {string} newName - Name for the duplicated point
   * @returns {Promise<Object>} Duplicated point
   */
  async function duplicatePoint(pointId, newName) {
    try {
      saving.value = true;

      const result = await brickSchemaService.points.duplicate(toValue(siteId), pointId, newName);

      if (!result.success) {
        throw new Error(result.error);
      }

      const duplicatedPoint = result.data.data || result.data;

      $q.notify({
        type: 'positive',
        message: `Point duplicated as "${newName}"`,
      });

      return duplicatedPoint;
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to duplicate point: ${error.message}`,
      });
      throw error;
    } finally {
      saving.value = false;
    }
  }

  return {
    // State
    loading,
    saving,

    // Methods
    loadPoints,
    createPoint,
    updatePoint,
    deletePoint,
    duplicatePoint,
  };
}
